const MAX_INTERVIEW_QUESTIONS = 6;

const ASSESSMENT_ONLY_LANGUAGE = /\b(record|upload|screen[ -]?share|select all|choose all|multiple[ -]?choice|rate your|rating scale|checkbox|video response)\b/i;

/**
 * Return the small, stable question shape covered by employer approval.
 *
 * Keeping this normalization shared between the full editor and the candidate
 * move gate prevents a question from being approved with a different hash than
 * the backend later validates before issuing an invitation.
 */
export const canonicalInterviewGoals = (goals) => (Array.isArray(goals) ? goals : [])
  .filter((goal) => String(goal?.text || '').trim())
  .slice(0, MAX_INTERVIEW_QUESTIONS)
  .map((goal, index) => ({
    id: String(goal.id || `goal-${index + 1}`),
    text: String(goal.text || '').trim().replace(/\s+/g, ' ')
  }));

/** Detect assessment-only prompts that cannot be used as live questions. */
export const requiresConversationalRegeneration = (questions) => !Array.isArray(questions)
  || !questions.length
  || questions.some((question) => (
    !String(question?.text || '').trim()
    || ![undefined, null, '', 'short-text'].includes(question?.type)
    || ASSESSMENT_ONLY_LANGUAGE.test(String(question?.text || ''))
  ));

/** Convert the persisted language allow-list into one employer-facing mode. */
export const interviewLanguageMode = (record) => {
  const languages = Array.isArray(record?.interviewLanguages) ? record.interviewLanguages : ['en'];
  if (languages.includes('en') && languages.includes('ar')) return 'candidate-choice';
  return languages.includes('ar') ? 'ar' : 'en';
};

/** Hash questions identically to the server-side invitation authorization. */
export const interviewGoalHash = async (goals) => {
  const payload = JSON.stringify(canonicalInterviewGoals(goals));
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Decide whether a candidate may move, an existing draft needs review, or new
 * job questions must be generated. Approval metadata is checked here for fast
 * UI gating; the backend independently verifies the hash before invitation.
 */
export const classifyInterviewQuestions = (jobRecord, baseQuestions = []) => {
  if (
    jobRecord?.status === 'approved'
    && jobRecord?.approvedAt
    && jobRecord?.approvedBy
    && jobRecord?.contentHash
    && !requiresConversationalRegeneration(jobRecord.questions)
  ) {
    return { state: 'ready', questions: canonicalInterviewGoals(jobRecord.questions) };
  }

  if (jobRecord && !requiresConversationalRegeneration(jobRecord.questions)) {
    return { state: 'review', questions: canonicalInterviewGoals(jobRecord.questions), source: jobRecord };
  }

  if (!jobRecord && !requiresConversationalRegeneration(baseQuestions)) {
    return { state: 'review', questions: canonicalInterviewGoals(baseQuestions), source: null };
  }

  return { state: 'generate', questions: [] };
};

